import { createAction } from "@reduxjs/toolkit";
import type { ModelFile } from "../../generated-axios-client/index.ts";
import { type AppDispatch, listener, type RootState } from "../../store.ts";
import { buildDownloadUrl } from "../downloadUrl.ts";
import {
  CHUNKS_STORE,
  clearZipExportData,
  idbGetAll,
  openZipExportDb,
  type StoredFileData,
} from "./idb.ts";
import type { ZipEntry, ZipWorkerEvent, ZipWorkerRequest } from "./protocol.ts";
import { ZipExportProgress } from "./ZipExportProgress.tsx";

export const downloadAllTicketFiles = createAction<{
  files: ModelFile[];
  /**
   * Names the archive after a single task instead of the whole ticket. Omitted for a
   * whole-ticket export; null for a task that has no number yet, which still has to be
   * told apart from the ticket's own archive.
   */
  taskNumber?: number | null;
}>("file/DOWNLOAD_ALL");

export const ZIP_EXPORT_NOTIFICATION_KEY = "zip-export-progress";

// per-tab concurrency guard; concurrent exports in a second tab would
// contend on the shared stores, which is an accepted limitation
let activeWorker: Worker | null = null;

export function dedupeZipNames(files: ModelFile[]): string[] {
  const usedNames = new Set<string>();
  return files.map((file, index) => {
    const baseName = file.name?.trim() || `file-${file.id ?? index}`;
    let candidate = baseName;
    let suffix = 1;
    while (usedNames.has(candidate)) {
      const dotIndex = baseName.lastIndexOf(".");
      candidate =
        dotIndex > 0
          ? `${baseName.slice(0, dotIndex)} (${suffix})${baseName.slice(dotIndex)}`
          : `${baseName} (${suffix})`;
      suffix += 1;
    }
    usedNames.add(candidate);
    return candidate;
  });
}

function buildTaskNamePart(taskNumber: number | null | undefined): string {
  if (taskNumber === undefined) {
    return "";
  }
  return taskNumber != null ? `-task-${taskNumber}` : "-task";
}

export async function checkQuota(totalBytes: number): Promise<boolean> {
  if (!navigator.storage?.estimate) {
    return true; // API unavailable — proceed best-effort
  }
  try {
    const estimate = await navigator.storage.estimate();
    if (estimate?.quota == null) {
      return true; // API present but gave no usable answer (Safari) — proceed best-effort
    }
    return estimate.quota - (estimate.usage ?? 0) >= totalBytes * 2;
  } catch {
    return true;
  }
}

async function assembleAndDownloadZip(zipName: string): Promise<void> {
  const db = await openZipExportDb();
  let zipBlob: Blob;
  try {
    // chunks are Blobs or raw bytes depending on what the browser could store
    // (see supportsBlobStorage); both are valid BlobParts
    const chunks = await idbGetAll<StoredFileData>(db, CHUNKS_STORE);
    zipBlob = new Blob(chunks as BlobPart[], { type: "application/zip" });
  } finally {
    db.close();
  }
  const objectUrl = URL.createObjectURL(zipBlob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = zipName;
  anchor.click();
  setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
    clearZipExportData().catch((error) => {
      console.error("[zip-export] failed to clear export data after download", error);
    });
  }, 10_000);
}

listener.startListening.withTypes<RootState, AppDispatch>()({
  actionCreator: downloadAllTicketFiles,
  effect: async (action, { getState }) => {
    const state = getState();
    const { notification, modal } = state.appFeedback;
    const intl = state.intl.datetimeIntl;
    const { files, taskNumber } = action.payload;

    if (activeWorker) {
      notification?.info({
        title: intl.formatMessage({ id: "ticket_files_export_running" }),
      });
      return;
    }
    if (files.length === 0) {
      return;
    }

    const totalBytes = files.reduce((sum, file) => sum + (file.size ?? 0), 0);
    if (!(await checkQuota(totalBytes))) {
      notification?.error({
        title: intl.formatMessage({ id: "ticket_files_export_no_space" }),
      });
      return;
    }

    const zipNames = dedupeZipNames(files);
    const entries: ZipEntry[] = files.map((file, index) => ({
      key: index,
      name: zipNames[index],
      url: buildDownloadUrl(file),
      size: file.size ?? null,
    }));

    const ticketNumber = state.ticket.ticket?.ticket_number;
    const ticketNamePart = ticketNumber != null ? `ticket-${ticketNumber}` : "ticket";
    const taskNamePart = buildTaskNamePart(taskNumber);
    const zipName = `${ticketNamePart}${taskNamePart}-files.zip`;

    // immediate feedback so a stalled export is visible instead of silent
    notification?.open({
      key: ZIP_EXPORT_NOTIFICATION_KEY,
      duration: 0,
      title: intl.formatMessage({ id: "ticket_files_export_downloading" }),
      description: (
        <ZipExportProgress
          percent={0}
          fileIndex={1}
          fileCount={files.length}
          phase="downloading"
        />
      ),
    });

    const clearExportData = () => {
      clearZipExportData().catch((error) => {
        console.error("[zip-export] failed to clear export data", error);
      });
    };

    let worker: Worker;
    try {
      worker = new Worker(new URL("./zipWorker.ts", import.meta.url), {
        type: "module",
      });
    } catch (error) {
      console.error("[zip-export] could not start the export worker", error);
      notification?.destroy(ZIP_EXPORT_NOTIFICATION_KEY);
      notification?.error({
        title: intl.formatMessage({ id: "ticket_files_export_failed" }),
      });
      return;
    }
    activeWorker = worker;

    const finishWorker = () => {
      // terminating first closes the worker's IDB connection
      // before any main-thread cleanup touches the stores
      worker.terminate();
      if (activeWorker === worker) {
        activeWorker = null;
      }
    };

    const failExport = (fileName?: string) => {
      notification?.destroy(ZIP_EXPORT_NOTIFICATION_KEY);
      finishWorker();
      notification?.error({
        title: intl.formatMessage({ id: "ticket_files_export_failed" }),
        description: fileName,
      });
      clearExportData();
    };

    worker.onmessage = (event: MessageEvent<ZipWorkerEvent>) => {
      const message = event.data;
      switch (message.type) {
        case "progress":
          notification?.open({
            key: ZIP_EXPORT_NOTIFICATION_KEY,
            duration: 0,
            title: intl.formatMessage({
              id:
                message.phase === "downloading"
                  ? "ticket_files_export_downloading"
                  : "ticket_files_export_zipping",
            }),
            description: (
              <ZipExportProgress
                percent={message.percent}
                fileIndex={message.fileIndex}
                fileCount={message.fileCount}
                phase={message.phase}
              />
            ),
          });
          break;
        case "done":
          notification?.destroy(ZIP_EXPORT_NOTIFICATION_KEY);
          finishWorker();
          modal?.confirm({
            title: intl.formatMessage({
              id: "ticket_files_export_ready_title",
            }),
            content: intl.formatMessage({
              id: "ticket_files_export_ready_content",
            }),
            okText: intl.formatMessage({ id: "ticket_files_export_download" }),
            cancelText: intl.formatMessage({ id: "cancel" }),
            onOk: () =>
              assembleAndDownloadZip(zipName).catch((error) => {
                console.error("[zip-export] failed to assemble the archive", error);
                notification?.error({
                  title: intl.formatMessage({ id: "ticket_files_export_failed" }),
                });
                clearExportData();
              }),
            onCancel: clearExportData,
          });
          break;
        case "error":
          console.error("[zip-export] worker reported a failure", {
            code: message.code,
            stage: message.stage,
            fileName: message.fileName,
            detail: message.detail,
          });
          failExport(message.fileName);
          break;
      }
    };
    // Safari can deliver a stripped ErrorEvent, so log the raw event alongside the
    // individual fields rather than relying on either one being populated
    worker.onerror = (event: ErrorEvent) => {
      console.error(
        "[zip-export] worker error event",
        event,
        {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
        event.error
      );
      failExport();
    };
    worker.onmessageerror = (event: MessageEvent) => {
      console.error("[zip-export] worker message could not be deserialized", event);
      failExport();
    };

    const startRequest: ZipWorkerRequest = {
      type: "start",
      entries,
      totalBytes,
    };
    worker.postMessage(startRequest);
  },
});

// wipe leftovers of an export interrupted by a page unload in a previous session
clearZipExportData().catch((error) => {
  console.error("[zip-export] failed to clear leftover export data on startup", error);
});
